import React, { createContext, useState, useContext, useEffect } from 'react';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [refreshToken, setRefreshToken] = useState(null);
  const [primeiroAcesso, setPrimeiroAcesso] = useState(false);
  const [userRoles, setUserRoles] = useState([]);
  const [loading, setLoading] = useState(true);

  // Função para carregar roles do usuário
  const carregarRolesUsuario = async (userId, token) => {
    const API_URL = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
    try {
      const response = await fetch(`${API_URL}/api/users/${userId}/roles`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        const roles = (data.dados?.roles || []).map(r => r.nome || r.role?.nome).filter(Boolean);
        
        // Log para debug
        if (process.env.NODE_ENV === 'development') {
          console.log('[carregarRolesUsuario] Roles carregadas:', {
            userId,
            rolesRaw: data.dados?.roles || [],
            rolesProcessed: roles,
            rolesCount: roles.length
          });
        }
        
        setUserRoles(roles);
        localStorage.setItem('user_roles', JSON.stringify(roles));
        
        // Avisar se não houver roles
        if (roles.length === 0) {
          console.warn('[carregarRolesUsuario] Usuário não possui roles atribuídas. Isso pode impedir acesso a funcionalidades administrativas.');
        }
      } else {
        // Se não for 401 (token expirado), apenas logar e continuar
        // Se for 401, verificar se é realmente token expirado antes de redirecionar
        if (response.status === 401) {
          try {
            const errorData = await response.json().catch(() => ({}));
            const mensagem = errorData?.mensagem || '';
            const isTokenExpired = 
              mensagem.includes('Token inválido') ||
              mensagem.includes('Token expirado') ||
              mensagem.includes('token inválido') ||
              mensagem.includes('token expirado');
            
            // Só redirecionar se for realmente token expirado
            if (isTokenExpired) {
              console.warn('Token expirado ao carregar roles. Limpando sessão...');
              // Limpar tokens e redirecionar para login
              localStorage.removeItem('access_token');
              localStorage.removeItem('refresh_token');
              localStorage.removeItem('user');
              localStorage.removeItem('primeiro_acesso');
              localStorage.removeItem('user_roles');
              window.location.href = '/login';
              return;
            }
          } catch (parseError) {
            // Se não conseguir parsear o erro, apenas continuar sem roles
            console.warn('Erro ao parsear resposta 401:', parseError);
          }
        }
        // Para outros erros (403, 404, etc), apenas não carregar roles
        console.warn(`Erro ao carregar roles: ${response.status} ${response.statusText}`);
        setUserRoles([]);
      }
    } catch (error) {
      // Erro de rede ou outro erro não relacionado a token
      console.error('Erro ao carregar roles do usuário:', error);
      setUserRoles([]);
    }
  };

  // Carregar dados do localStorage ao inicializar
  useEffect(() => {
    const storedToken = localStorage.getItem('access_token');
    const storedRefreshToken = localStorage.getItem('refresh_token');
    const storedUser = localStorage.getItem('user');
    const storedPrimeiroAcesso = localStorage.getItem('primeiro_acesso');
    const storedRoles = localStorage.getItem('user_roles');

    if (storedToken && storedUser) {
      setAccessToken(storedToken);
      setRefreshToken(storedRefreshToken);
      const parsedUser = JSON.parse(storedUser);
      setUser(parsedUser);
      // Converter string para boolean corretamente
      // localStorage sempre retorna string, então 'false' !== true
      const primeiroAcessoValue = storedPrimeiroAcesso === 'true';
      setPrimeiroAcesso(primeiroAcessoValue);
      
      // Carregar roles do usuário
      if (storedRoles) {
        try {
          setUserRoles(JSON.parse(storedRoles));
        } catch (e) {
          console.error('Erro ao carregar roles do localStorage:', e);
          setUserRoles([]);
        }
      } else {
        // Se não houver roles no localStorage, carregar do servidor
        if (storedToken && parsedUser?.id) {
          carregarRolesUsuario(parsedUser.id, storedToken);
        }
      }
    }
    setLoading(false);
  }, []);

  const login = async (tokenData, userData, isPrimeiroAcesso) => {
    // Garantir que seja boolean
    const primeiroAcessoBool = isPrimeiroAcesso === true || isPrimeiroAcesso === 'true';

    setAccessToken(tokenData.access_token);
    setRefreshToken(tokenData.refresh_token);
    setUser(userData);
    setPrimeiroAcesso(primeiroAcessoBool);

    // Salvar no localStorage
    localStorage.setItem('access_token', tokenData.access_token);
    localStorage.setItem('refresh_token', tokenData.refresh_token);
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('primeiro_acesso', primeiroAcessoBool.toString());

    // Carregar roles do usuário após login
    if (userData?.id && !primeiroAcessoBool) {
      await carregarRolesUsuario(userData.id, tokenData.access_token);
    }
  };

  const logout = async () => {
    const apiUrl = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
    
    // Tentar fazer logout no servidor
    if (accessToken) {
      try {
        await fetch(`${apiUrl}/api/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          }
        });
      } catch (error) {
        console.error('Erro ao fazer logout no servidor:', error);
      }
    }

    // Limpar estado local
    setAccessToken(null);
    setRefreshToken(null);
    setUser(null);
    setPrimeiroAcesso(false);
    setUserRoles([]);

    // Limpar localStorage
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    localStorage.removeItem('primeiro_acesso');
    localStorage.removeItem('user_roles');
  };

  const updateTokens = (newAccessToken) => {
    setAccessToken(newAccessToken);
    localStorage.setItem('access_token', newAccessToken);
  };

  const updatePrimeiroAcesso = (value) => {
    setPrimeiroAcesso(value);
    localStorage.setItem('primeiro_acesso', value.toString());
  };

  // Função para verificar se o usuário tem uma role específica
  const hasRole = (roleName) => {
    if (!userRoles || userRoles.length === 0) return false;
    return userRoles.some(role => 
      role.toLowerCase() === roleName.toLowerCase()
    );
  };

  // Função para verificar se o usuário tem alguma das roles especificadas
  const hasAnyRole = (roleNames) => {
    if (!userRoles || userRoles.length === 0) return false;
    return roleNames.some(roleName => 
      userRoles.some(role => role.toLowerCase() === roleName.toLowerCase())
    );
  };

  const value = {
    user,
    accessToken,
    refreshToken,
    primeiroAcesso,
    userRoles,
    loading,
    login,
    logout,
    updateTokens,
    updatePrimeiroAcesso,
    hasRole,
    hasAnyRole,
    isAuthenticated: !!accessToken
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

